import { z } from 'zod/v4'

export type ObjectDefinitionProperty = {
  type: 'string' | 'number' | 'boolean' | 'object' | 'array'
  required: boolean
  description: string
  properties?: Record<string, ObjectDefinitionProperty> // For nested objects
  items?: ObjectDefinitionProperty // For array items
  enum?: string[] | number[] | boolean[] // For enumerated values
  format?: string // For special string formats (e.g., 'date', 'email', etc.)
  minimum?: number // For number validation
  maximum?: number // For number validation
  minLength?: number // For string/array validation
  maxLength?: number // For string/array validation
  pattern?: string // For string regex pattern
}

/**
 * Detects if a schema is in ObjectDefinitionProperty format vs JSON Schema format.
 * ObjectDefinitionProperty format has properties with `required: boolean` on each property.
 * JSON Schema format has `type: 'object'` at the root with a `properties` object and `required` array.
 */
export function isObjectDefinitionFormat(
  schema: Record<string, unknown>,
): schema is Record<string, ObjectDefinitionProperty> {
  // JSON Schema format has type: 'object' at root level
  if (schema.type === 'object' && schema.properties) {
    return false
  }

  // ObjectDefinitionProperty format: each top-level key is a property with type and required
  const keys = Object.keys(schema)
  if (keys.length === 0) return false

  return keys.every((key) => {
    const prop = schema[key]
    return (
      typeof prop === 'object' &&
      prop !== null &&
      'type' in prop &&
      'required' in prop &&
      typeof (prop as ObjectDefinitionProperty).required === 'boolean'
    )
  })
}

/**
 * Converts ObjectDefinitionProperty format to JSON Schema format.
 * This allows using the simpler ObjectDefinitionProperty format in YAML configs
 * while still being compatible with jsonSchemaToZod.
 */
export function objectDefinitionToJsonSchema(
  definition: Record<string, ObjectDefinitionProperty>,
): Record<string, unknown> {
  const convertProperty = (
    prop: ObjectDefinitionProperty,
  ): Record<string, unknown> => {
    const result: Record<string, unknown> = {
      type: prop.type,
    }

    if (prop.description) {
      result.description = prop.description
    }

    if (prop.enum) {
      result.enum = prop.enum
    }

    if (prop.format) {
      result.format = prop.format
    }

    if (prop.minimum !== undefined) {
      result.minimum = prop.minimum
    }

    if (prop.maximum !== undefined) {
      result.maximum = prop.maximum
    }

    if (prop.minLength !== undefined) {
      result.minLength = prop.minLength
    }

    if (prop.maxLength !== undefined) {
      result.maxLength = prop.maxLength
    }

    if (prop.pattern) {
      result.pattern = prop.pattern
    }

    // Handle nested objects
    if (prop.type === 'object' && prop.properties) {
      const nestedProperties: Record<string, unknown> = {}
      const nestedRequired: string[] = []

      for (const [key, nestedProp] of Object.entries(prop.properties)) {
        nestedProperties[key] = convertProperty(nestedProp)
        if (nestedProp.required) {
          nestedRequired.push(key)
        }
      }

      result.properties = nestedProperties
      result.required = nestedRequired
    }

    // Handle arrays
    if (prop.type === 'array' && prop.items) {
      result.items = convertProperty(prop.items)
    }

    return result
  }

  const properties: Record<string, unknown> = {}
  const required: string[] = []

  for (const [key, prop] of Object.entries(definition)) {
    properties[key] = convertProperty(prop)
    if (prop.required) {
      required.push(key)
    }
  }

  return {
    type: 'object',
    properties,
    required,
  }
}

/**
 * Normalizes a schema to JSON Schema format.
 * Accepts either ObjectDefinitionProperty format or JSON Schema format.
 * Returns JSON Schema format.
 */
export function normalizeToJsonSchema(
  schema: Record<string, unknown>,
): Record<string, unknown> {
  if (isObjectDefinitionFormat(schema)) {
    return objectDefinitionToJsonSchema(
      schema as Record<string, ObjectDefinitionProperty>,
    )
  }
  return schema
}

/**
 * Generates a Zod schema from an object definition.
 *
 * Provider-specific behavior for optional fields:
 * - OpenAI (strict mode): Uses `.nullable()` - field must be present but can be null.
 *   OpenAI requires ALL properties in the JSON schema's `required` array.
 * - Google/Anthropic (standard): Uses `.nullish()` - field can be omitted or null.
 *   These providers follow standard JSON Schema conventions.
 *
 * @param workflowStepObjectDefinition - The object definition to convert
 * @param providerSlug - The LLM provider slug ('openai', 'google', 'anthropic'). Defaults to 'openai'.
 */
export const generateZodSchemaFromObjectDefinition = (
  workflowStepObjectDefinition: Record<string, ObjectDefinitionProperty>,
  providerSlug: string = 'openai',
): z.ZodSchema => {
  // OpenAI requires strict mode where all properties must be in the required array
  // Other providers (Google, Anthropic) follow standard JSON Schema conventions
  const useStrictMode = providerSlug === 'openai'

  // Create a recursive function to handle nested properties
  // The `isRequired` parameter determines if we should make the field nullable/nullish
  const createZodSchema = (
    propDef: ObjectDefinitionProperty,
    isRequired: boolean = true,
  ): z.ZodTypeAny => {
    let schema: z.ZodTypeAny

    // Handle enums first for all types
    if (propDef.enum && propDef.enum.length > 0) {
      switch (propDef.type) {
        case 'string':
          // Cast the enum values to string[] and ensure it has at least one value
          const stringEnumValues = propDef.enum.map((v) => String(v)) as [
            string,
            ...string[],
          ]
          schema = z.enum(stringEnumValues).describe(propDef.description || '')
          // For optional enum fields, wrap based on provider
          if (!isRequired) {
            return useStrictMode ? schema.nullable() : schema.nullish()
          }
          return schema

        case 'number':
          // For number enums, use a refined type
          const numericEnum = propDef.enum as number[]
          schema = z
            .number()
            .refine((n) => numericEnum.includes(n), {
              message: `Value must be one of: ${numericEnum.join(', ')}`,
            })
            .describe(propDef.description || '')
          if (!isRequired) {
            return useStrictMode ? schema.nullable() : schema.nullish()
          }
          return schema

        case 'boolean':
          // For boolean enums, use a refined type
          const booleanEnum = propDef.enum as boolean[]
          schema = z
            .boolean()
            .refine((b) => booleanEnum.includes(b), {
              message: `Value must be one of: ${booleanEnum.join(', ')}`,
            })
            .describe(propDef.description || '')
          if (!isRequired) {
            return useStrictMode ? schema.nullable() : schema.nullish()
          }
          return schema
      }
    }

    switch (propDef.type) {
      case 'string':
        schema = z.string().describe(propDef.description || '')

        // Add string-specific validations
        if (propDef.format === 'email') {
          schema = (schema as z.ZodString).email()
        } else if (propDef.format === 'url') {
          schema = (schema as z.ZodString).url()
        }
        if (propDef.pattern) {
          schema = (schema as z.ZodString).regex(new RegExp(propDef.pattern))
        }
        if (propDef.minLength !== undefined) {
          schema = (schema as z.ZodString).min(propDef.minLength)
        }
        if (propDef.maxLength !== undefined) {
          schema = (schema as z.ZodString).max(propDef.maxLength)
        }
        break

      case 'number':
        schema = z.number().describe(propDef.description || '')

        // Add number-specific validations
        if (propDef.minimum !== undefined) {
          schema = (schema as z.ZodNumber).min(propDef.minimum)
        }
        if (propDef.maximum !== undefined) {
          schema = (schema as z.ZodNumber).max(propDef.maximum)
        }
        break

      case 'boolean':
        schema = z.boolean().describe(propDef.description || '')
        break

      case 'object':
        if (propDef.properties) {
          const objectProperties: Record<string, z.ZodTypeAny> = {}

          Object.entries(propDef.properties).forEach(([key, nestedProp]) => {
            // Pass the nested property's required flag to createZodSchema
            objectProperties[key] = createZodSchema(
              nestedProp,
              nestedProp.required,
            )
          })

          schema = z
            .object(objectProperties)
            .describe(propDef.description || '')
        } else {
          // If no properties defined, use an empty object
          // Note: z.record() generates propertyNames in JSON Schema which OpenAI doesn't support
          // Using z.object({}) is safer for OpenAI compatibility
          schema = z.object({}).describe(propDef.description || '')
        }
        break

      case 'array':
        if (propDef.items) {
          // Array items are always required within the array
          let arraySchema = z.array(createZodSchema(propDef.items, true))

          // Add array-specific validations
          if (propDef.minLength !== undefined) {
            arraySchema = arraySchema.min(propDef.minLength)
          }
          if (propDef.maxLength !== undefined) {
            arraySchema = arraySchema.max(propDef.maxLength)
          }

          schema = arraySchema.describe(propDef.description || '')
        } else {
          // If no items defined, use array of unknown
          schema = z.array(z.unknown()).describe(propDef.description || '')
        }
        break

      default:
        schema = z.unknown().describe(propDef.description || '')
    }

    // For optional fields:
    // - OpenAI (strict mode): use nullable() - field must be present but can be null
    // - Other providers: use nullish() - field can be omitted or null
    if (!isRequired) {
      schema = useStrictMode ? schema.nullable() : schema.nullish()
    }

    return schema
  }

  // Initialize a properties object for the zod schema
  const properties: Record<string, z.ZodTypeAny> = {}

  // Iterate through the object definition properties
  Object.entries(workflowStepObjectDefinition).forEach(([key, propDef]) => {
    // Pass the property's required flag to createZodSchema
    properties[key] = createZodSchema(propDef, propDef.required)
  })

  // Return the complete schema
  return z.object(properties)
}
